import { ArrowRightIcon, CopyIcon, TrashIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { CellProps, Column } from "react-table";

import {
  ConnectionBridgeBaseFragment,
  useDeleteConnectionBridgeMutation,
} from "@/apollo/types";
import ConfirmDeleteModal from "@/components/elements/ConfirmDeleteModal";
import { createStickyMenuColumn } from "@/components/elements/DataTable/helpers/create-sticky-menu-column";
import DefaultTable from "@/components/elements/DefaultTable";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";
import { FormatTime } from "@/components/elements/FormatTime";
import IntegrationCellTitle from "@/components/elements/IntegrationCellTitle";
import MenuItem from "@/components/elements/MenuItem";
import Tooltip from "@/components/elements/Tooltip";
import TableMenu from "@/components/modules/TableMenu";
import { usePermission } from "@/features/roleAccess";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { useToast } from "@/providers/ToastProvider";
import { LinkWithSlug } from "@/router";

type Props = {
  data: ConnectionBridgeBaseFragment[];
  onDeleted?: () => void;
  loading?: boolean;
};

type Row = ConnectionBridgeBaseFragment;

export function AuthorizationLinksTable({ data, onDeleted, loading }: Props) {
  const [, copy] = useCopyToClipboard();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteBridge] = useDeleteConnectionBridgeMutation();
  const toast = useToast();
  const canDelete = usePermission("deleteConnection");

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        Header: "Connector",
        accessor: "integrationId",
        Cell: ({ value }: CellProps<Row, Row["integrationId"]>) => (
          <IntegrationCellTitle integrationId={value} />
        ),
      },
      {
        Header: "Label",
        accessor: "label",
        Cell: ({ value, row }: CellProps<Row, Row["label"]>) => (
          <div className={"flex items-center gap-1 text-sm text-foreground"}>
            {row.original.connectionId ? (
              <LinkWithSlug
                to={`/settings/connections/${row.original.connectionId}`}
                className="flex items-center underline underline-offset-2"
              >
                <ArrowRightIcon className={"size-4"} />
                {value}
              </LinkWithSlug>
            ) : (
              <span>{value ?? "-"}</span>
            )}
          </div>
        ),
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }: CellProps<Row, Row["createdAt"]>) => (
          <Tooltip content={<FormatTime date={value} format="LLL" />}>
            <FormatRelativeTime date={value} />
          </Tooltip>
        ),
      },
      {
        Header: "Expires",
        accessor: "expiresAt",
        Cell: ({ value }: CellProps<Row, Row["expiresAt"]>) => (
          <Tooltip content={<FormatTime date={value} format="LLL" />}>
            <FormatRelativeTime date={value} />
          </Tooltip>
        ),
      },
      createStickyMenuColumn({
        Cell: ({ row }: CellProps<Row>) => (
          <TableMenu>
            <MenuItem
              text="Copy Authorization Link"
              icon={<CopyIcon className="size-4" />}
              onClick={async () => {
                await copy(row.original.authorizeUrl);
                toast("Copied", "Authorization link copied", "success");
              }}
            />
            {canDelete && (
              <MenuItem
                text="Delete"
                danger={true}
                icon={<TrashIcon className="size-4" />}
                onClick={() => setDeleteId(row.original.id)}
              />
            )}
          </TableMenu>
        ),
      }),
    ],
    [copy, toast, canDelete],
  );

  return (
    <div className="w-full max-w-(--breakpoint-xl)">
      <DefaultTable
        columns={columns}
        data={data}
        isSortable={false}
        isLoading={loading}
      />
      <ConfirmDeleteModal
        title="Authorization link"
        show={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={async () => {
          if (!deleteId) {
            return;
          }
          await deleteBridge({ variables: { id: deleteId } });
          setDeleteId(null);
          toast("Deleted", "Authorization link deleted", "success");
          onDeleted?.();
        }}
      />
    </div>
  );
}
